import React, { useEffect, useState } from "react";

import { useGetCropMixComposerSettings, useLocation, useMutateCropMixComposerSettings, useNavigate, useTranslation } from "utils";

import { Flex, Form, Input, Modal, Option, Radio, Select } from "components";

import { getNextAvailableMixName } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export const CropMixSelectionModal = ({ basePath, showModal }: { basePath: string; showModal: boolean }) => {
  const { t } = useTranslation("cropMixComposer");
  const navigate = useNavigate();
  const location = useLocation();

  const { data: settingsCMC, isPending: isPendingSettingsCMC, isFetching: isFetchingSettingsCMC } = useGetCropMixComposerSettings();
  const { mutateAsync: mutateAsyncCropMixComposerSettings } = useMutateCropMixComposerSettings();

  const [form] = Form.useForm<{
    newMixName: string;
    selectedMixId: string;
  }>();
  const [shouldCreateNewMix, setShouldCreateNewMix] = useState<boolean>(true);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async () => {
    form
      .validateFields()
      .then(async () => {
        setIsSubmitting(true);

        const newMixName = form.getFieldValue("newMixName");

        if (shouldCreateNewMix) {
          const bodyRequest = { data: { name: newMixName } };

          if (settingsCMC && settingsCMC.length > 0) {
            Object.assign(bodyRequest.data, {
              recommendation: {
                settings: {
                  soilType: settingsCMC[0].recommendation?.settings?.soilType || undefined,
                  soilPh: settingsCMC[0].recommendation?.settings?.soilPh || undefined,
                  isDrought:
                    typeof settingsCMC[0].recommendation?.settings?.isDrought === "boolean" ? settingsCMC[0].recommendation?.settings?.isDrought : undefined,
                },
              },
            });
          }

          const newMix = await mutateAsyncCropMixComposerSettings(bodyRequest);
          if (newMix) {
            navigate(`${basePath}cmc/${newMix.id}${location.search}`);
          }
        } else {
          const selectedMixId = form.getFieldValue("selectedMixId");
          navigate(`${basePath}cmc/${selectedMixId}${location.search}`);
        }
      })
      .catch((e: Error) => {
        console.error(e); // eslint-disable-line
      })
      .finally(() => {
        // reset the radio button selection
        setShouldCreateNewMix(true);
        setIsSubmitting(false);
      });
  };

  const defaultMixName = getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC);

  useEffect(() => {
    const currentValue = form.getFieldValue("newMixName");
    // Only update if the field is empty or still has the previous default value
    if (!currentValue || currentValue.startsWith(t("cropMixSelectionModal.myMix"))) {
      form.setFieldValue("newMixName", defaultMixName);
    }
    // Set the first mix as default selection
    form.setFieldValue("selectedMixId", settingsCMC?.[0]?.id);
  }, [settingsCMC, t, form, defaultMixName]);

  return (
    <Form form={form} name="cropMixSelection">
      <Modal
        dataTestId="crop-mix-selection-modal"
        cancelText={t("cropMixSelectionModal.cancel")}
        onCancel={() => {
          navigate(basePath === "/" ? "/home" : basePath);
          // reset the radio button selection and the form values
          setShouldCreateNewMix(true);
          form.resetFields();
        }}
        okText={t("cropMixSelectionModal.start")}
        onOk={handleSubmit}
        showModal={showModal}
        title={t("cropMixSelectionModal.title")}
        loading={isPendingSettingsCMC || isFetchingSettingsCMC || isSubmitting}
        content={
          <Radio.Group
            defaultValue={shouldCreateNewMix}
            value={shouldCreateNewMix}
            onChange={() => {
              setShouldCreateNewMix((prevState) => !prevState);
              // reset form when switching between options
              form.resetFields();
            }}
            style={{ width: "100%" }}
          >
            <Flex vertical gap={"2rem"}>
              <Flex vertical gap={"1rem"}>
                <Radio value={true} data-testid={"cmc-modal-start-new-mix-radio-button"}>
                  {t("cropMixSelectionModal.startNew")}
                </Radio>
                {shouldCreateNewMix && (
                  <Input
                    dataTestId="cmc-modal-start-new-mix-name-input"
                    name="newMixName"
                    label={t("cropMixSelectionModal.mixName")}
                    initialValue={defaultMixName}
                    rules={[
                      { required: true },
                      {
                        validator: (_, value) => {
                          if (settingsCMC?.some((mix) => mix.name === value)) return Promise.reject(new Error(t("cropMixSelectionModal.errors.mixNameUnique")));
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                )}
              </Flex>
              <Flex vertical gap={"1rem"}>
                <Radio value={false} data-testid={"cmc-modal-open-existing-mix-radio-button"} disabled={!settingsCMC?.length}>
                  {t("cropMixSelectionModal.openExisting")}
                </Radio>
                {!shouldCreateNewMix && (
                  // @ts-expect-error need to convert to tsx
                  <Select
                    dataTestId={"cmc-modal-open-existing-mix-select-menu"}
                    label={t("cropMixSelectionModal.selectMix")}
                    name={"selectedMixId"}
                    initialValue={settingsCMC?.[0]?.id}
                    rules={[{ required: true, message: t("cropMixSelectionModal.errors.mustSelectMix") }]}
                  >
                    {settingsCMC?.map((mix) => (
                      <Option key={mix.id} value={mix.id}>
                        {mix.name}
                      </Option>
                    ))}
                  </Select>
                )}
              </Flex>
            </Flex>
          </Radio.Group>
        }
      />
    </Form>
  );
};
